import { FunctionComponent } from 'react'
import styles from './timePicker.module.scss'
import globalStyles from '@/styles/components.module.scss'
import { renderTimeViewClock, TimePicker, TimeView } from '@mui/x-date-pickers'
import { Accessibility, AccessibilityType } from '@/types/Daycare'
import { useTranslation } from 'react-i18next'
import { format, parse, setHours, setMinutes, setSeconds } from 'date-fns'

interface TimePickerComponentProps {
    label?: string
    value: string
    setValue: (date: string) => void
    disabled?: boolean
    className?: string
    minuteToDisable?: number
    accessibilities?: Accessibility[]
    morning?: boolean
    minHour?: number
    minMinute?: number
    maxHour?: number
    maxMinute?: number
    readOnly?: boolean
    parent?: boolean
    startTime?: string
    endTime?: string
    error?: boolean
}

const TimePickerComponent: FunctionComponent<TimePickerComponentProps> = ({
    label,
    value,
    setValue,
    disabled = false,
    className,
    minuteToDisable = 15,
    accessibilities = [],
    morning = false,
    minHour = 0,
    minMinute = 0,
    maxHour = 23,
    maxMinute = 59,
    readOnly = false,
    parent = false,
    startTime,
    endTime,
    error
}: TimePickerComponentProps) => {
    const { t } = useTranslation()

    function changeHour(date: Date | null): void {
        if (date) {
            let hours: number = date.getHours()
            let minutes: number = date.getMinutes()

            if (hours < minHour) hours = minHour
            else if (hours > maxHour) hours = maxHour

            if (minutes % minuteToDisable !== 0) minutes -= minutes % minuteToDisable

            const newDate: Date = setSeconds(setMinutes(setHours(date, hours), minutes), 0)
            setValue(format(newDate, 'HH:mm'))
        }
    }

    function disableTime(
        timeValue: Date,
        clockType: TimeView,
        accessibilities: Accessibility[],
        morning: boolean,
        minuteStep: number
    ): boolean {
        if (accessibilities.length === 0) {
            return clockType === 'minutes' && timeValue.getMinutes() % minuteStep !== 0
        }

        const slots: Accessibility[] = accessibilities.filter(
            (a: Accessibility) => a.type === (morning ? AccessibilityType.arrival : AccessibilityType.departure)
        )

        if (slots.length === 0) {
            return clockType === 'minutes' && timeValue.getMinutes() % minuteStep !== 0
        }

        const hour: number = timeValue.getHours()
        const minute: number = timeValue.getMinutes()
        const total: number = hour * 60 + minute

        const inAnySlot: (t: number) => boolean = (t: number): boolean =>
            slots.some(({ start, end }: Accessibility) => {
                const [sh, sm] = start.split(':').map(Number)
                const [eh, em] = end.split(':').map(Number)
                return t >= sh * 60 + sm && t <= eh * 60 + em
            })

        if (clockType === 'minutes') {
            if (minute % minuteStep !== 0) return true
            return !inAnySlot(total)
        }

        if (clockType === 'hours') {
            for (let m: number = 0; m < 60; m += minuteStep) {
                if (inAnySlot(hour * 60 + m)) return false
            }
            return true
        }

        return false
    }

    const minTime: Date = startTime
        ? parse(startTime, 'HH:mm', new Date())
        : setSeconds(setMinutes(setHours(new Date(), minHour), minMinute), 0)

    const maxTime: Date = endTime
        ? parse(endTime, 'HH:mm', new Date())
        : setSeconds(setMinutes(setHours(new Date(), maxHour), maxMinute), 0)

    return (
        <TimePicker
            ampm={false}
            localeText={{
                cancelButtonLabel: t('cancel')
            }}
            viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock
            }}
            label={label}
            views={minuteToDisable < 60 ? ['hours', 'minutes'] : ['hours']}
            value={value !== '' ? parse(value, 'HH:mm', new Date()) : null}
            onChange={changeHour}
            disabled={disabled}
            readOnly={readOnly}
            className={`${className} ${parent ? styles.Parent : styles.Daycare} ${error ? globalStyles.FieldInError : ''}`}
            slotProps={{
                textField: {
                    error
                }
            }}
            shouldDisableTime={(value: Date, view: TimeView) =>
                disableTime(value, view, accessibilities, morning, minuteToDisable)
            }
            minTime={minTime}
            maxTime={maxTime}
        />
    )
}

export default TimePickerComponent
